import React from 'react';
import { 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  CheckCircle, 
  Error, 
  Pending, 
  CreditCard, 
  Receipt, 
  Send 
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  backgroundColor: 'rgba(30, 30, 35, 0.8)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  marginBottom: theme.spacing(3),
  color: 'white'
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-active': {
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    },
  },
}));

// Custom step icon component
const StepIcon = ({ active, completed, icon }) => {
  const icons = {
    1: <CreditCard />,
    2: <Send />,
    3: <Pending />,
    4: <Receipt />
  };

  if (completed) {
    return <CheckCircle color="success" />;
  }

  if (active) {
    return (
      <Box position="relative" display="inline-flex">
        <CircularProgress size={24} />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {icons[icon]}
        </Box>
      </Box>
    );
  }

  return icons[icon];
};

/**
 * Component to display payment status with a stepper UI
 * @param {Object} props
 * @param {string} props.status - Current payment status
 * @param {string} props.message - Status message
 * @param {Object} props.error - Error object if any
 * @param {boolean} props.isLoading - Whether payment is being processed
 * @param {string} props.transactionId - Transaction ID if available
 */
const PaymentStatusDisplay = ({ 
  status, 
  message, 
  error, 
  isLoading, 
  transactionId,
  onClose
}) => {
  // Determine active step based on status
  let activeStep = 0;
  
  switch (status) {
    case 'initiated':
      activeStep = 0;
      break;
    case 'processing':
    case 'pending':
    case 'pending_payment':
      activeStep = 1;
      break;
    case 'verifying':
      activeStep = 2;
      break;
    case 'completed':
      activeStep = 3;
      break;
    case 'failed':
      activeStep = 1; // Show as failed at processing step
      break;
    default:
      activeStep = 0;
  }

  // Steps in the payment process
  const steps = [
    'Payment Initiated',
    'Processing Payment',
    'Verifying Transaction',
    'Payment Completed'
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <StyledPaper>
          <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
            Payment Status
          </Typography>
          
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StyledStepLabel 
                  StepIconComponent={(props) => 
                    <StepIcon 
                      {...props} 
                      active={index === activeStep} 
                      completed={index < activeStep || (index === 3 && status === 'completed')}
                    />
                  }
                >
                  {label}
                </StyledStepLabel>
              </Step>
            ))}
          </Stepper>
          
          <Box mt={4} textAlign="center">
            {isLoading ? (
              <CircularProgress size={24} sx={{ mb: 2 }} />
            ) : status === 'failed' ? (
              <Error color="error" sx={{ fontSize: 40, mb: 2 }} />
            ) : status === 'completed' ? (
              <CheckCircle color="success" sx={{ fontSize: 40, mb: 2 }} />
            ) : (
              <Pending color="primary" sx={{ fontSize: 40, mb: 2 }} />
            )}
            
            <Typography variant="body1" gutterBottom>
              {message || `Payment is ${status || 'being processed'}`}
            </Typography>
            
            {transactionId && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, color: 'rgba(255,255,255,0.6)' }}>
                Transaction ID: {transactionId}
              </Typography>
            )}
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mt: 2, 
                  backgroundColor: 'rgba(211, 47, 47, 0.15)',
                  color: '#f44336',
                  '& .MuiAlert-icon': {
                    color: '#f44336'
                  }
                }}
              >
                {error.message || 'An error occurred during payment processing'}
              </Alert>
            )}
          </Box>
        </StyledPaper>
      </motion.div>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={Boolean(message && !error && status !== 'failed')}
        autoHideDuration={6000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={onClose} 
          severity={status === 'completed' ? 'success' : 'info'}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentStatusDisplay;
